// Facet filtering functionality
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.setupEventListeners();

    // Setup popover after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupPopover();
        this.attachFibreFilterListeners();
        this.updateFilterCount();
        this.storeOriginalColorOrder();
      });
    } else {
      this.setupPopover();
      this.attachFibreFilterListeners();
      this.updateFilterCount();
      this.storeOriginalColorOrder();
    }
  }

  setupEventListeners() {
    // Handle form submissions for non-fibre filters
    const form = this.querySelector('#FacetFiltersForm');
    const sortForm = document.getElementById('FacetSortForm');

    if (form) {
      // Handle checkbox changes for regular filters
      form.addEventListener('change', (e) => {
        if (
          e.target.type === 'checkbox' &&
          !e.target.name.includes('filter.v.option.fibre') &&
          !e.target.name.includes('filter.p.m.custom.fibre_new')
        ) {
          e.preventDefault();
          this.handleCheckboxChange(e.target);
        }

        // Handle price range radio button changes
        if (e.target.type === 'radio' && e.target.dataset.action === 'price-range') {
          e.preventDefault();
          this.handlePriceRangeChange(e.target);
        }
      });

      // Handle form submissions (fallback)
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleFilterChange();
      });
    }

    // Handle sort form
    if (sortForm) {
      const sortSelect = document.getElementById('SortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => {
          e.preventDefault();
          this.showLoader();

          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortSelect.value);

          // Update URL without page refresh
          const newUrl = url.toString();
          history.pushState({ path: newUrl }, '', newUrl);

          // Fetch and update product grid
          this.fetchAndUpdateProducts(newUrl);
        });
      }
    }

    // Attach initial event listeners
    this.attachEventListeners();

    // Handle browser back/forward buttons
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.path) {
        this.showLoader();
        this.fetchAndUpdateProducts(e.state.path);
      }
    });

    // Update filter count on window resize (for responsive fibre filter counting)
    window.addEventListener('resize', () => {
      this.updateFilterCount();
    });
  }

  // Function to handle fibre filter selection
  selectFibreOnly(radioButton) {
    // Validate the radio button
    if (!radioButton || !radioButton.name || !radioButton.value) {
      console.error('Invalid radio button passed to selectFibreOnly:', radioButton);
      return;
    }

    // Show loader
    this.showLoader();

    // Get the current URL
    const url = new URL(window.location.href);

    // Create a new URL to the collection page
    const collectionUrl = document.querySelector('[data-collection-url]')?.dataset.collectionUrl || '';
    let newUrl = `${collectionUrl}?`;

    // Add the fibre filter parameter
    newUrl += `${radioButton.name}=${encodeURIComponent(radioButton.value)}`;

    // Preserve all other existing parameters except fibre filters
    for (const [key, value] of url.searchParams.entries()) {
      // Skip the fibre filter we're setting and any existing fibre filters
      if (
        key !== radioButton.name &&
        !key.includes('filter.v.option.fibre') &&
        !key.includes('filter.p.m.custom.fibre_new')
      ) {
        newUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    }

    // Update URL without page refresh
    history.pushState({ path: newUrl }, '', newUrl);

    // Fetch and update product grid
    this.fetchAndUpdateProducts(newUrl);
  }

  selectFibreAll() {
    // Show loader
    this.showLoader();

    // Get the current URL
    const url = new URL(window.location.href);

    // Create a new URL to the collection page
    const collectionUrl =
      document.querySelector('[data-collection-url]')?.dataset.collectionUrl || window.location.pathname;
    let newUrl = collectionUrl;

    // Add a query character if we'll have parameters
    let hasParams = false;

    // Preserve all existing parameters except fibre filters
    for (const [key, value] of url.searchParams.entries()) {
      // Skip any fibre filters but keep search terms, sort, etc.
      if (
        !key.includes('filter.v.option.fibre') &&
        !key.includes('filter.p.m.custom.fibre') &&
        !key.includes('filter.p.m.custom.fibre_new')
      ) {
        if (!hasParams) {
          newUrl += '?';
          hasParams = true;
        } else {
          newUrl += '&';
        }
        newUrl += `${key}=${encodeURIComponent(value)}`;
      }
    }

    // Update URL without page refresh
    history.pushState({ path: newUrl }, '', newUrl);

    // Fetch and update product grid
    this.fetchAndUpdateProducts(newUrl);
  }

  // Show loader function
  showLoader() {
    const loader = document.getElementById('ProductGridLoader');
    if (loader) {
      loader.classList.remove('hidden');
      loader.classList.add('flex');
    }
  }

  // Hide loader function
  hideLoader() {
    const loader = document.getElementById('ProductGridLoader');
    if (loader) {
      loader.classList.remove('flex');
      loader.classList.add('hidden');
    }
  }

  // Function to fetch and update products
  fetchAndUpdateProducts(url) {
    // Remember if popover was open
    const currentPopover = document.getElementById('filters-popover');
    const wasPopoverOpen = currentPopover && !currentPopover.classList.contains('hidden');

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Mirror the robots meta tag from the fetched page
        this.syncRobotsMetaTag(doc);

        // Update product grid
        const newProductGrid = doc.getElementById('ProductGridContainer');
        const currentProductGrid = document.getElementById('ProductGridContainer');

        if (newProductGrid && currentProductGrid) {
          currentProductGrid.innerHTML = newProductGrid.innerHTML;

          // Reinitialize review ratings for new product cards
          if (typeof window.initializeProductReviews === 'function') {
            window.initializeProductReviews(currentProductGrid);
          }
        }

        // Update collection banner when navigating to different collection
        const newBanner = doc.getElementById('shopify-section-banner');
        const currentBanner = document.getElementById('shopify-section-banner');

        if (newBanner && currentBanner) {
          currentBanner.innerHTML = newBanner.innerHTML;
        }

        // Update facets but preserve popover state
        const newFacets = doc.querySelector('facet-filters-form');
        const currentFacets = document.querySelector('facet-filters-form');

        if (newFacets && currentFacets) {
          const currentForm = currentFacets.querySelector('#FacetFiltersForm');
          const newForm = newFacets.querySelector('#FacetFiltersForm');

          if (newForm && currentForm) {
            // Save popover state before updating
            const currentPopover = document.getElementById('filters-popover');
            let preservedPopoverHTML = '';

            if (currentPopover && wasPopoverOpen) {
              // Store the entire popover container
              const popoverContainer = currentPopover.parentElement;
              preservedPopoverHTML = popoverContainer.outerHTML;
            }

            // Update the form content
            currentForm.innerHTML = newForm.innerHTML;

            // If popover was open, restore it by finding and replacing the new popover
            if (wasPopoverOpen && preservedPopoverHTML) {
              const newPopoverTrigger = document.getElementById('filters-popover-trigger');
              if (newPopoverTrigger) {
                const newPopoverContainer = newPopoverTrigger.parentElement;
                newPopoverContainer.outerHTML = preservedPopoverHTML;
              }
            }

            // Always update popover content with new filter states (whether it was open or closed)
            const currentPopoverContent = document.querySelector('#filters-popover');
            const newPopoverContent = newFacets.querySelector('#filters-popover');

            if (currentPopoverContent && newPopoverContent) {
              // Update the inner content with the latest filter states
              const currentInner = currentPopoverContent.querySelector('div');
              const newInner = newPopoverContent.querySelector('div');

              if (currentInner && newInner) {
                currentInner.innerHTML = newInner.innerHTML;
              }
            }
          }

          // Restore color swatch order
          this.restoreColorOrder();

          // Reattach event listeners
          this.attachEventListeners();

          // Update filter count
          this.updateFilterCount();
        }

        // Hide loader
        this.hideLoader();
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        this.hideLoader();
      });
  }

  // Keep the robots meta tag in step with the page the server just returned,
  // which decides indexability from more than the sort and filter parameters.
  syncRobotsMetaTag(doc) {
    const newRobotsMeta = doc.querySelector('meta[name="robots"]');
    let robotsMeta = document.querySelector('meta[name="robots"]');

    if (newRobotsMeta) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', newRobotsMeta.getAttribute('content'));
    } else if (robotsMeta) {
      robotsMeta.remove();
    }
  }

  // Handle individual checkbox changes
  handleCheckboxChange(checkbox) {
    this.showLoader();

    // Get all current form data to preserve multiple selections
    const form = this.querySelector('#FacetFiltersForm');
    const formData = new FormData(form);
    const url = new URL(window.location.href);

    // Clear existing filter parameters except fibre filters and search terms
    const searchParams = url.searchParams;
    const preservedParams = new URLSearchParams();

    // Preserve search terms and fibre filters
    for (const [key, value] of searchParams.entries()) {
      if (
        key === 'q' ||
        key === 'options[prefix]' ||
        key.includes('filter.v.option.fibre') ||
        key.includes('filter.p.m.custom.fibre_new') ||
        key === 'sort_by'
      ) {
        preservedParams.append(key, value);
      }
    }

    // Add all current filter selections from form
    for (const [key, value] of formData.entries()) {
      if (
        key !== 'q' &&
        key !== 'options[prefix]' &&
        !key.includes('filter.v.option.fibre') &&
        !key.includes('filter.p.m.custom.fibre_new')
      ) {
        preservedParams.append(key, value);
      }
    }

    // Construct new URL
    const newUrl = `${url.pathname}?${preservedParams.toString()}`;
    history.pushState({ path: newUrl }, '', newUrl);

    // Fetch and update product grid
    this.fetchAndUpdateProducts(newUrl);
  }

  // Handle price range radio button changes
  handlePriceRangeChange(radio) {
    this.showLoader();

    const minValue = radio.dataset.min;
    const maxValue = radio.dataset.max;
    const minParam = radio.dataset.minParam;
    const maxParam = radio.dataset.maxParam;

    const url = new URL(window.location.href);

    // Remove existing price range params
    url.searchParams.delete(minParam);
    url.searchParams.delete(maxParam);

    // Add new price range params
    if (minValue) {
      url.searchParams.set(minParam, minValue);
    }
    if (maxValue) {
      url.searchParams.set(maxParam, maxValue);
    }

    const newUrl = url.toString();
    history.pushState({ path: newUrl }, '', newUrl);
    this.fetchAndUpdateProducts(newUrl);
  }

  // Handle regular filter changes
  handleFilterChange() {
    this.showLoader();

    const form = this.querySelector('#FacetFiltersForm');
    const formData = new FormData(form);
    const url = new URL(window.location.href);

    // Clear existing filter parameters except fibre filters and search terms
    const searchParams = url.searchParams;
    const preservedParams = new URLSearchParams();

    // Preserve search terms and fibre filters
    for (const [key, value] of searchParams.entries()) {
      if (
        key === 'q' ||
        key === 'options[prefix]' ||
        key.includes('filter.v.option.fibre') ||
        key.includes('filter.p.m.custom.fibre_new') ||
        key === 'sort_by'
      ) {
        preservedParams.append(key, value);
      }
    }

    // Add all current filter selections from form
    for (const [key, value] of formData.entries()) {
      if (
        key !== 'q' &&
        key !== 'options[prefix]' &&
        !key.includes('filter.v.option.fibre') &&
        !key.includes('filter.p.m.custom.fibre_new')
      ) {
        preservedParams.append(key, value);
      }
    }

    // Construct new URL
    const newUrl = `${url.pathname}?${preservedParams.toString()}`;
    history.pushState({ path: newUrl }, '', newUrl);

    // Fetch and update product grid
    this.fetchAndUpdateProducts(newUrl);
  }

  // Setup popover functionality
  setupPopover() {
    // Setup filters popover
    const filtersTrigger = document.getElementById('filters-popover-trigger');
    const filtersPopover = document.getElementById('filters-popover');

    if (filtersTrigger && filtersPopover) {
      this.setupSinglePopover(filtersTrigger, filtersPopover, 'filters');
    }

    // Setup sort popover
    const sortTrigger = document.getElementById('sort-popover-trigger');
    const sortPopover = document.getElementById('sort-popover');

    if (sortTrigger && sortPopover) {
      this.setupSinglePopover(sortTrigger, sortPopover, 'sort');
    }
  }

  setupSinglePopover(trigger, popover, type) {
    // Remove existing listeners to prevent duplicates
    if (this[`handle${type}PopoverToggle`]) {
      trigger.removeEventListener('click', this[`handle${type}PopoverToggle`]);
    }

    // Store bound function for removal later
    this[`handle${type}PopoverToggle`] = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Close other popovers first
      this.closeAllPopovers(type);

      const isOpen = !popover.classList.contains('hidden');

      if (isOpen) {
        this.closePopover(trigger, popover);
      } else {
        this.openPopover(trigger, popover);
      }
    };

    // Add the toggle listener
    trigger.addEventListener('click', this[`handle${type}PopoverToggle`]);

    // Remove and re-add document listeners to prevent duplicates
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    if (this.documentKeyHandler) {
      document.removeEventListener('keydown', this.documentKeyHandler);
    }

    // Store bound handlers
    this.documentClickHandler = (e) => {
      const filtersPopover = document.getElementById('filters-popover');
      const filtersTrigger = document.getElementById('filters-popover-trigger');
      const sortPopover = document.getElementById('sort-popover');
      const sortTrigger = document.getElementById('sort-popover-trigger');

      // Check if click is outside both popovers
      const isOutsideFilters = !filtersTrigger?.contains(e.target) && !filtersPopover?.contains(e.target);
      const isOutsideSort = !sortTrigger?.contains(e.target) && !sortPopover?.contains(e.target);

      if (isOutsideFilters && filtersPopover && !filtersPopover.classList.contains('hidden')) {
        this.closePopover(filtersTrigger, filtersPopover);
      }
      if (isOutsideSort && sortPopover && !sortPopover.classList.contains('hidden')) {
        this.closePopover(sortTrigger, sortPopover);
      }
    };

    this.documentKeyHandler = (e) => {
      if (e.key === 'Escape') {
        const filtersPopover = document.getElementById('filters-popover');
        const filtersTrigger = document.getElementById('filters-popover-trigger');
        const sortPopover = document.getElementById('sort-popover');
        const sortTrigger = document.getElementById('sort-popover-trigger');

        if (filtersPopover && !filtersPopover.classList.contains('hidden')) {
          this.closePopover(filtersTrigger, filtersPopover);
          filtersTrigger?.focus();
        }
        if (sortPopover && !sortPopover.classList.contains('hidden')) {
          this.closePopover(sortTrigger, sortPopover);
          sortTrigger?.focus();
        }
      }
    };

    // Add document listeners
    document.addEventListener('click', this.documentClickHandler);
    document.addEventListener('keydown', this.documentKeyHandler);
  }

  closeAllPopovers(except = null) {
    const filtersPopover = document.getElementById('filters-popover');
    const filtersTrigger = document.getElementById('filters-popover-trigger');
    const sortPopover = document.getElementById('sort-popover');
    const sortTrigger = document.getElementById('sort-popover-trigger');

    if (except !== 'filters' && filtersPopover && !filtersPopover.classList.contains('hidden')) {
      this.closePopover(filtersTrigger, filtersPopover);
    }
    if (except !== 'sort' && sortPopover && !sortPopover.classList.contains('hidden')) {
      this.closePopover(sortTrigger, sortPopover);
    }
  }

  openPopover(trigger, popover) {
    // Position the popover below the trigger button for filters popover
    // if (popover.id === 'filters-popover') {
    //   const triggerRect = trigger.getBoundingClientRect();
    //   popover.style.top = `${triggerRect.bottom + 8}px`; // 8px = mt-2 equivalent
    // }

    popover.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');

    // Focus first interactive element in popover
    const firstInput = popover.querySelector('input, button, a');
    if (firstInput) {
      firstInput.focus();
    }
  }

  closePopover(trigger, popover) {
    popover.classList.add('hidden');
    trigger.setAttribute('aria-expanded', 'false');
  }

  // Store original color swatch order
  storeOriginalColorOrder() {
    // Look for color filter by checking for the specific ul structure with rounded-full labels
    const colorFilterContainer = this.querySelector('fieldset ul.w-full.relative.flex');
    if (!colorFilterContainer) return;

    // Check if this is a color filter by looking for rounded-full labels (specific to color swatches)
    const hasRoundedLabels = colorFilterContainer.querySelector('label.rounded-full');
    if (!hasRoundedLabels) return;

    const colorSwatches = colorFilterContainer.querySelectorAll('li input[type="checkbox"]');
    if (colorSwatches.length === 0) return;

    // Store the original order by value
    this.originalColorOrder = [];
    colorSwatches.forEach((input) => {
      if (input.value && input.name) {
        this.originalColorOrder.push({
          value: input.value,
          paramName: input.name,
        });
      }
    });
  }

  // Restore color swatch order after AJAX updates
  restoreColorOrder() {
    if (!this.originalColorOrder || this.originalColorOrder.length === 0) return;

    const colorFilterContainer = this.querySelector('fieldset ul.w-full.relative.flex');
    if (!colorFilterContainer) return;

    // Verify this is actually a color filter by checking for rounded-full labels
    const hasRoundedLabels = colorFilterContainer.querySelector('label.rounded-full');
    if (!hasRoundedLabels) return;

    // Check if this is a color filter by looking for color swatches
    const colorSwatches = colorFilterContainer.querySelectorAll('li');
    if (colorSwatches.length === 0) return;

    // Create a map of current swatches by value
    const currentSwatches = new Map();
    colorSwatches.forEach((li) => {
      const input = li.querySelector('input[type="checkbox"]');
      if (input && input.value) {
        currentSwatches.set(input.value, li);
      }
    });

    // Reorder based on original order
    const fragment = document.createDocumentFragment();
    let reorderedCount = 0;

    // Add swatches in original order
    this.originalColorOrder.forEach((originalItem) => {
      const swatch = currentSwatches.get(originalItem.value);
      if (swatch) {
        fragment.appendChild(swatch);
        currentSwatches.delete(originalItem.value);
        reorderedCount++;
      }
    });

    // Add any remaining swatches that weren't in the original order
    currentSwatches.forEach((swatch) => {
      fragment.appendChild(swatch);
    });

    // Only update if we actually reordered some items
    if (reorderedCount > 0) {
      // Find the clear button (last li that contains an <a> with svg) and preserve it
      const clearButtons = colorFilterContainer.querySelectorAll('li a svg');
      let clearButtonLi = null;
      if (clearButtons.length > 0) {
        // Get the last one (should be the clear button)
        const lastClearButton = clearButtons[clearButtons.length - 1];
        clearButtonLi = lastClearButton.closest('li');
      }

      // Clear the container
      colorFilterContainer.innerHTML = '';

      // Add reordered swatches
      colorFilterContainer.appendChild(fragment);

      // Re-add the clear button if it existed
      if (clearButtonLi) {
        colorFilterContainer.appendChild(clearButtonLi);
      }
    }
  }

  // Count and update the filter count display
  updateFilterCount() {
    const countElement = document.getElementById('filters-count');
    if (!countElement) return;

    let activeFilters = 0;

    // Count active filters in the popover
    const popover = document.getElementById('filters-popover');
    if (popover) {
      // Count checked checkboxes in popover (excluding fibre filters)
      const checkedBoxes = popover.querySelectorAll('input[type="checkbox"]:checked');
      activeFilters += checkedBoxes.length;

      // Count active price range radio buttons
      const priceRangeRadios = popover.querySelectorAll('input[data-action="price-range"]:checked');
      activeFilters += priceRangeRadios.length;

      // Count active price range text inputs (legacy support)
      const priceInputs = popover.querySelectorAll('input[type="number"], input[type="range"]');
      priceInputs.forEach((input) => {
        if (input.value && input.value !== '' && input.value !== '0') {
          activeFilters += 0.5; // Count as half since min/max are usually paired
        }
      });
    }

    // Round in case of price range half-counts
    activeFilters = Math.round(activeFilters);

    // Update the display
    if (activeFilters > 0) {
      countElement.textContent = `[${activeFilters}]`;
      countElement.classList.remove('hidden');
    } else {
      countElement.classList.add('hidden');
    }
  }

  // Attach fibre filter specific listeners
  attachFibreFilterListeners() {
    // Clear any existing listeners first
    if (this.fibreEventListeners) {
      this.fibreEventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
    }
    this.fibreEventListeners = [];

    const form = document.getElementById('FacetFiltersForm');
    if (!form) return;

    // Handle buttons with onclick handlers for fibre filters within the form
    form.querySelectorAll('[onclick*="selectFibre"]').forEach((button) => {
      const onclickAttr = button.getAttribute('onclick');
      if (!onclickAttr) return;

      button.removeAttribute('onclick');

      if (onclickAttr.includes('selectFibreOnly')) {
        // Extract the radio button parameter from the onclick
        const match = onclickAttr.match(/selectFibreOnly\(([^)]+)\)/);
        if (match) {
          const handler = (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Find the associated radio button by ID or selector
            const radioSelector = match[1].replace(/['"`]/g, ''); // Remove quotes
            const radioButton =
              document.getElementById(radioSelector) ||
              document.querySelector(radioSelector) ||
              button.querySelector('input[type="radio"]') ||
              button.closest('fieldset')?.querySelector('input[type="radio"]');

            if (radioButton && radioButton.name && radioButton.value) {
              // Set the radio button as checked first
              radioButton.checked = true;
              this.selectFibreOnly(radioButton);
            }
          };

          button.addEventListener('click', handler);
          this.fibreEventListeners.push({ element: button, event: 'click', handler });
        }
      } else if (onclickAttr.includes('selectFibreAll')) {
        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Check if this button has a data-url or href for navigation
          const targetUrl = button.dataset.url || button.getAttribute('href');
          if (targetUrl) {
            // Use the specific URL if provided
            this.showLoader();
            history.pushState({ path: targetUrl }, '', targetUrl);
            this.fetchAndUpdateProducts(targetUrl);
          } else {
            // Fallback to the selectFibreAll method
            this.selectFibreAll();
          }
        };

        button.addEventListener('click', handler);
        this.fibreEventListeners.push({ element: button, event: 'click', handler });
      }
    });

    // Handle radio buttons with fibre in their name attribute
    form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio.name && (radio.name.includes('fibre') || radio.name.includes('filter.p.m.custom.fibre_new'))) {
        const handler = (e) => {
          if (e.target.checked) {
            this.selectFibreOnly(e.target);
          }
        };

        radio.addEventListener('change', handler);
        this.fibreEventListeners.push({ element: radio, event: 'change', handler });
      }
    });
  }

  // Function to attach event listeners
  attachEventListeners() {
    const form = document.getElementById('FacetFiltersForm');
    if (form) {
      // Handle all filter labels for non-fibre filters
      form.querySelectorAll('label[for]').forEach((label) => {
        // Remove any existing onclick handlers
        label.removeAttribute('onclick');

        // Add click listener for labels that target checkboxes
        const checkbox = document.getElementById(label.getAttribute('for'));
        if (
          checkbox &&
          checkbox.type === 'checkbox' &&
          !checkbox.name.includes('filter.v.option.fibre') &&
          !checkbox.name.includes('filter.p.m.custom.fibre_new')
        ) {
          label.addEventListener('click', (e) => {
            e.preventDefault();
            checkbox.checked = !checkbox.checked;
            this.handleCheckboxChange(checkbox);
          });
        }
      });

      // Handle direct checkbox clicks for non-fibre filters
      form.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        if (
          !checkbox.name.includes('filter.v.option.fibre') &&
          !checkbox.name.includes('filter.p.m.custom.fibre_new')
        ) {
          checkbox.addEventListener('change', (e) => {
            this.handleCheckboxChange(e.target);
          });
        }
      });

      // Handle price range radio button clicks
      form.querySelectorAll('input[data-action="price-range"]').forEach((radio) => {
        radio.addEventListener('change', (e) => {
          this.handlePriceRangeChange(e.target);
        });

        // Also handle label clicks for price range radios
        const label = form.querySelector(`label[for="${radio.id}"]`);
        if (label) {
          label.addEventListener('click', (e) => {
            e.preventDefault();
            radio.checked = true;
            this.handlePriceRangeChange(radio);
          });
        }
      });
    }

    // Handle clear filter links and other filter navigation links
    const filterLinks = document.querySelectorAll('a[href]');
    filterLinks.forEach((link) => {
      const href = link.getAttribute('href');
      const linkText = link.textContent.toLowerCase().trim();

      // Exclude header filter dropdown links from being intercepted
      const isHeaderFilterLink = link.closest('[id^="HeaderFilter-"]') !== null;

      // Exclude header menu links from being intercepted
      const isHeaderMenuLink = link.closest('[id^="HeaderMenu-"]') !== null;

      // Exclude reviews anchor links from being intercepted
      const isReviewsLink = href.startsWith('#reviews');

      // Exclude product links from being intercepted
      const isProductLink = href.includes('/products/');

      // Check if this is a filter-related link
      const isFilterLink =
        !isHeaderFilterLink &&
        !isHeaderMenuLink &&
        !isReviewsLink &&
        !isProductLink &&
        (href.includes('filter.') ||
          link.closest('facet-filters-form') ||
          link.closest('#FacetFiltersForm') ||
          linkText.includes('clear') ||
          linkText.includes('all') ||
          (link.querySelector('svg') && (linkText.includes('clear') || linkText === '')) ||
          link.hasAttribute('data-filter-clear'));

      if (isFilterLink) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.showLoader();

          // Update URL without page refresh
          history.pushState({ path: href }, '', href);

          // Fetch and update product grid
          this.fetchAndUpdateProducts(href);
        });
      }
    });

    // Re-setup popover after content updates
    this.setupPopover();

    // Reattach fibre filter listeners
    this.attachFibreFilterListeners();
  }
}

// Define the custom element
customElements.define('facet-filters-form', FacetFiltersForm);

// Global functions for backwards compatibility with inline handlers
window.selectFibreOnly = function (radioButton) {
  const facetForm = document.querySelector('facet-filters-form');
  if (facetForm) {
    facetForm.selectFibreOnly(radioButton);
  }
};

window.selectFibreAll = function () {
  const facetForm = document.querySelector('facet-filters-form');
  if (facetForm) {
    facetForm.selectFibreAll();
  }
};

// Price range functionality
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);
